import React from 'react';
import { motion } from 'framer-motion';
import {
  FaBolt,
  FaCode,
  FaCrosshairs,
  FaCube,
  FaLayerGroup,
  FaMicrochip
} from 'react-icons/fa';
import './Technology.css';

const technologies = [
  {
    id: 'propulsion',
    icon: FaBolt,
    title: 'Propulsion Systems',
    description:
      'Engine architecture research exploring liquid propellant cycles optimized for responsive launch cadence and cost efficiency. Early thrust chamber and nozzle design studies underway.',
    phase: 'Research',
    phaseVariant: 'cyan',
    tags: ['Engine cycle analysis', 'Thrust chamber', 'Propellant selection']
  },
  {
    id: 'gnc',
    icon: FaCrosshairs,
    title: 'Guidance, Navigation & Control',
    description:
      'Six-DOF trajectory simulation and autonomous guidance algorithms for precision orbital insertion. Navigation filter design and sensor fusion architecture in progress.',
    phase: 'Concept Development',
    phaseVariant: 'cyan',
    tags: ['6-DOF modeling', 'Navigation filter', 'Attitude algorithms']
  },
  {
    id: 'flight-sw',
    icon: FaCode,
    title: 'Flight Software',
    description:
      'Embedded real-time software for vehicle health monitoring, command processing, fault detection, and mission sequence management. Core architecture validated on hardware.',
    phase: 'Prototype',
    phaseVariant: 'amber',
    tags: ['RTOS', 'Telemetry stack', 'Fault management']
  },
  {
    id: 'structures',
    icon: FaLayerGroup,
    title: 'Vehicle Structures',
    description:
      'Structural design and finite element analysis using lightweight composite and metallic materials. Buckling, vibration, and thermal load cases under evaluation.',
    phase: 'Design Study',
    phaseVariant: 'teal',
    tags: ['FEA analysis', 'Composites', 'Thermal modeling']
  },
  {
    id: 'avionics',
    icon: FaMicrochip,
    title: 'Avionics & Electronics',
    description:
      'Custom avionics for telemetry, command/data handling, power management, and payload interface. Radiation-tolerant component selection under evaluation.',
    phase: 'Research',
    phaseVariant: 'cyan',
    tags: ['Power systems', 'Command & data handling', 'Rad-hard design']
  },
  {
    id: 'payload',
    icon: FaCube,
    title: 'Payload Integration',
    description:
      'Standardized CubeSat dispenser interfaces and environmental qualification workflows for RPSLV rideshare manifests. Integration procedures and mass properties accounting in development.',
    phase: 'Early Validation',
    phaseVariant: 'green',
    tags: ['CubeSat interface', 'Separation systems', 'Mass accounting']
  }
];

const sectionVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.05 }
  }
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] }
  }
};

const cardVariants = {
  hidden: { opacity: 0, y: 28, scale: 0.97 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
  }
};

const Technology = () => (
  <section id="technology" className="tech-section" aria-labelledby="tech-heading">
    <div className="tech-section__container">
      <motion.div
        className="tech-section__intro"
        variants={sectionVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-80px' }}
      >
        <motion.span className="section-kicker" variants={fadeUp}>Engineering systems</motion.span>
        <motion.h2 id="tech-heading" className="section-heading" variants={fadeUp}>
          The technology stack <span>we are building.</span>
        </motion.h2>
        <motion.p className="section-copy" variants={fadeUp}>
          Every system is researched and designed in-house — from propulsion to flight software.
          No shortcuts, no black boxes, no fabricated milestones.
        </motion.p>
      </motion.div>

      <motion.div
        className="tech-section__grid"
        variants={sectionVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-50px' }}
      >
        {technologies.map((tech) => {
          const Icon = tech.icon;
          return (
            <motion.article
              key={tech.id}
              className="tech-card glass-card"
              variants={cardVariants}
            >
              <div className="tech-card__header">
                <div className={`tech-card__icon tech-card__icon--${tech.phaseVariant}`}>
                  <Icon aria-hidden="true" />
                </div>
                <span className={`tech-card__phase tech-card__phase--${tech.phaseVariant}`}>
                  {tech.phase}
                </span>
              </div>

              <h3 className="tech-card__title">{tech.title}</h3>
              <p className="tech-card__desc">{tech.description}</p>

              <div className="tech-card__tags">
                {tech.tags.map((tag) => (
                  <span key={tag} className="tech-card__tag">{tag}</span>
                ))}
              </div>
            </motion.article>
          );
        })}
      </motion.div>

      <motion.div
        className="tech-section__note glass-card"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-40px' }}
        transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="tech-section__note-dot" aria-hidden="true" />
        <p>
          All systems are in active research or early development. Phase labels reflect honest
          engineering progress — not marketing status. No artificial readiness percentages.
        </p>
      </motion.div>
    </div>
  </section>
);

export default Technology;
